/*
 * Copyright 1999-2024 Jagex Ltd.
 */
import { observer } from 'mobx-react-lite';
import React, { FunctionComponent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteProps, Route, RouteComponentProps, useLocation } from 'react-router-dom';
import { AppNotifications } from '@components/appNotifications/AppNotifications';
import { Loader } from '@components/layouts';
import { I18nFacade } from '@i18n/I18nFacade';
import { AuthStatus } from '@stores/auth/types';
import { LoadingState } from '@stores/loading/types';
import { noop } from '@utils/helpers';
import { useStore, useQuery } from '@utils/hooks';
import { getLastTargetRoute, setTargetRoute } from '@utils/navigation';

type Props = Omit<RouteProps, 'component'> & {
  component: FunctionComponent<RouteComponentProps>;
};

export const ProtectedRoute: FunctionComponent<Props> = observer(({ component: Component, ...restProps }) => {
  const { auth, users, loading } = useStore();
  const { authStatus, isAuthenticated } = auth;
  const {
    i18n: { language: lang }
  } = useTranslation();
  const location = useLocation();
  const isLoading = loading.isLoading('users--get-user-account-selection');
  const query = useQuery();

  useEffect(() => {
    const checkAuth = async () => {
      if (authStatus === AuthStatus.UNKNOWN) {
        await auth.checkAuthStatus();
      }

      if (
        [AuthStatus.UNAUTHORIZED, AuthStatus.FORBIDDEN].includes(authStatus) &&
        !loading.isLoading('auth-check-status')
      ) {
        setTargetRoute(location);
        const { loginLink } = await auth.getLoginLink(lang);
        const parsedLoginLink = new URL(loginLink);

        if (query.steamNative) {
          parsedLoginLink.searchParams.set('steamNative', query.steamNative as string);
        }

        if (authStatus === AuthStatus.FORBIDDEN) {
          parsedLoginLink.searchParams.set('prompt', 'login');
        }

        I18nFacade.saveCurrentLocale(lang);

        window.location.assign(parsedLoginLink.toString());
      }
    };

    checkAuth();
  }, [auth, authStatus, lang, loading, location, users, query]);

  useEffect(() => {
    const selectAccount = async () => {
      if (isAuthenticated && !users.isCharacterSelected) {
        const config = {
          params: {
            lastTargetRoute: getLastTargetRoute(),
            locale: lang
          }
        };

        await users.getUserAccountSelection(config).catch(noop);
      } else {
        loading.setLoaderState('users--get-user-account-selection', LoadingState.STOPPED);
      }
    };

    selectAccount();
  }, [isAuthenticated, loading, users, lang]);

  // TODO: change hiding unnecessary display of errors not related to authorization
  return isAuthenticated && users.isCharacterSelected && !isLoading ? (
    <Route {...restProps} render={props => <Component {...props} />} />
  ) : (
    <Loader>{authStatus === AuthStatus.UNAUTHORIZED && <AppNotifications />}</Loader>
  );
});
